import React, { useState, useEffect } from 'react';
import './Auth.css';

function OTPVerification({ email, onVerify, onResend, onBack, verificationType = 'signup' }) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP input
  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only take last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    
    setOtp(newOtp);
    
    // Focus last filled input or first empty
    const nextIndex = Math.min(pastedData.length, 5);
    document.getElementById(`otp-${nextIndex}`).focus();
  };

  // Submit OTP
  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onVerify(otpCode);
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0').focus();
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    setLoading(true);
    setError('');

    try {
      await onResend();
      setTimeLeft(600);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0').focus();
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#040000,#0e0101)",padding:"20px"}}>
      <div className="auth-container">
      <div className="auth-box">
        <h2>Verify Your Email</h2>
        <p className="otp-instruction">
          We've sent a 6-digit code to<br />
          <strong>{email}</strong>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="otp-input"
                autoFocus={index === 0}
              />
            ))}
          </div>

          {error && <p className="error-message">{error}</p>}

          <div className="otp-timer">
            {timeLeft > 0 ? (
              <p>Code expires in <strong>{formatTime(timeLeft)}</strong></p>
            ) : (
              <p className="expired">Code expired!</p>
            )}
          </div>

          <button type="submit" className="auth-submit-btn" disabled={loading || otp.join('').length !== 6} style={{fontSize:"18px",padding:"16px",letterSpacing:"2px",marginTop:"16px",width:"100%"}}>
            {loading ? 'Verifying...' : 'Verify Code'}
          </button>

          <div className="otp-actions">
            {canResend ? (
              <button type="button" className="link-button" onClick={handleResend} disabled={loading}>
                Resend Code
              </button>
            ) : (
              <p className="resend-info" style={{fontSize:"16px"}}>Resend available in {formatTime(timeLeft)}</p>
            )}

            <button type="button" className="link-button" onClick={onBack}>
              ← Back
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>
  );
}

export default OTPVerification;